/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { Button } from '@/components/ui/button';
import { Money } from '@/domain/controlling/entities';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeftToArc } from '@fortawesome/pro-regular-svg-icons';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTagebuchEinstellungen } from '@/domain/controlling/hooks/useTagebuchEinstellungen';
import { useDialogContext } from '../DialogContext';
import { formatNumber } from '../../../utils';
import { useFormSubmit } from '../useFormSubmit';
import { useStandortContext } from '@/features/standortContext/StandortContext';

export function PrognoseUebernehmenButton({
  umsatzPrognose,
  absatzPrognose,
  type,
}: {
  type: 'umsatz' | 'absatz';
  umsatzPrognose?: Money;
  absatzPrognose?: number;
}) {
  const { t } = useTranslation();
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { umsatzplanungAnzeige } = useTagebuchEinstellungen(standortId);
  const { dispatch } = useDialogContext();
  const { handleSubmit } = useFormSubmit(onSuccess, onError);

  function onSuccess() {
    dispatch({ type: 'updateSubmit', isSubmitting: false });
    dispatch({
      type: 'updateDialogMessage',
      dialogMessage: {
        type: 'success',
        message: t('domain.controlling.umsatzplanung.planung.prognose.success'),
      },
    });
  }

  function onError() {
    dispatch({
      type: 'updateDialogMessage',
      dialogMessage: {
        type: 'error',
        message: t('domain.controlling.umsatzplanung.planung.prognose.error'),
      },
    });
  }

  function handleUmsatzPrognoseUebernehmen(
    event: React.MouseEvent<HTMLButtonElement>,
  ) {
    dispatch({
      type: 'updateUmsatzPlan',
      values: {
        umsatzPlan: formatNumber(umsatzPrognose.amount.toString(), false),
      },
    });
    handleSubmit(event);
  }

  function handleAbsatzPrognoseUebernehmen(
    event: React.MouseEvent<HTMLButtonElement>,
  ) {
    dispatch({
      type: 'updateAbsatzPlan',
      values: {
        absatzPlan: formatNumber(absatzPrognose.toString(), true),
      },
    });
    handleSubmit(event);
  }

  function isPrognoseUebernehmenButtonDisabled() {
    if (
      umsatzplanungAnzeige === 'UMSATZ_ABSATZ_PRODUKTIVITAET' &&
      !umsatzPrognose &&
      !absatzPrognose
    ) {
      return true;
    }
    if (umsatzplanungAnzeige === 'UMSATZ_PRODUKTIVITAET' && !umsatzPrognose) {
      return true;
    }
    if (umsatzplanungAnzeige === 'ABSATZ_PRODUKTIVITAET' && !absatzPrognose) {
      return true;
    }
    return false;
  }

  function PrognoseIconButton() {
    return (
      <Button
        className="text-primary-blue-B600 disabled:text-neutrals-N500"
        disabled={isPrognoseUebernehmenButtonDisabled()}
        variant="icon"
        size="icon"
        onClick={(e) => {
          if (type === 'umsatz') {
            handleUmsatzPrognoseUebernehmen(e);
          } else if (type === 'absatz') {
            handleAbsatzPrognoseUebernehmen(e);
          }
        }}>
        <FontAwesomeIcon icon={faArrowLeftToArc} />
      </Button>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger className="w-fit">
          <PrognoseIconButton />
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="bg-white p-2 m-2 rounded-md text-xs shadow border border-neutrals-N300 text-neutrals-N900 w-fit">
          {isPrognoseUebernehmenButtonDisabled()
            ? t('domain.controlling.umsatzplanung.planung.prognose.tooltip')
            : t(
                'domain.controlling.umsatzplanung.planung.prognose.buttonLabel',
              )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
